import {sendAnalyticsEvent} from "./analyticsEventClient";

/**
 * What a page view says, and what it may never say (kitecorp/transferify-api-service#1929) —
 * React-free, so every rule below is unit-testable against real addresses.
 *
 * gtag used to send `page_view` on its own, from `config` plus GA4's history-change measurement. With
 * no GA4 tag on the page nothing does, so this module is the replacement — and building the address
 * ourselves is an improvement rather than a cost, because gtag reported the WHOLE URL.
 *
 * ── The address is origin + pathname, and nothing else ───────────────────────────────────────────
 * No query string: it carries `?gclid=`, `?booking=` and `?token=` values that identify a person or
 * authorize an action. No fragment, and that one is not a matter of degree — an end-to-end-encrypted
 * transfer keeps its FILE KEY in the fragment, so a page view carrying it would hand the decryption
 * key to an analytics property.
 *
 * ── Identifier-shaped segments are redacted ──────────────────────────────────────────────────────
 * `/download/<capability>` is a share capability, which is a secret; `/activate/<token>` is a
 * single-use credential; `/reminder-uploads/<reminderId>/<contactId>` names two rows that belong to a
 * person. Those three trees are named below because their shape is a decision, not a guess. Every
 * OTHER segment is still tested against {@link identifierShaped}, so a route added later with an id in
 * its path is redacted the day it ships rather than the day somebody remembers this file.
 *
 * `vitals/routeClass.ts` answers a neighbouring question — which BUCKET a reading belongs to — and
 * deliberately sends no path at all, so there was nothing there to reuse. The two stay separate: a
 * CloudWatch series must not grow with traffic, while a GA4 page report is expected to name pages.
 */

/** What a redacted segment reads as. Spelled like a route parameter, which is what an analyst wants. */
export const REDACTED_PATH_SEGMENT = ":id";

/**
 * The route trees whose remaining segments are ALWAYS redacted, whatever they look like.
 *
 * Shape-based redaction alone would not be enough for these: a share capability is an opaque string
 * this app does not get to make assumptions about, and "it did not look like an id" is not an argument
 * for publishing a secret.
 */
const IDENTIFIER_BEARING_SEGMENTS: ReadonlySet<string> = new Set([
	"download",
	"reminder-uploads",
	"activate",
]);

/** A canonical UUID, the form this product mints every external id in. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * How long an opaque-looking segment may be before it is treated as an identifier.
 *
 * Length alone cannot tell a token from a page name here. The longest static segment this app serves is
 * 43 characters (`dateien-zwischen-zwei-computern-uebertragen`, under `/de/ratgeber`) and most of the
 * markdown corpus is past sixteen, so the DIGIT is the test that separates the two: a page name is
 * words, a token carries numbers. This floor is what keeps a SHORT page name that legitimately has a
 * digit in it — `/p2p` — out of the digit test's reach.
 *
 * Two content slugs do carry a digit past the floor (`transfer-fisiere-mari-20gb`,
 * `20-gb-und-mehr-versenden`) and are reported as `:id`. The rule errs in that direction rather than
 * the other: a redacted page name costs one row on a report; a published token costs a person.
 */
const MIN_OPAQUE_TOKEN_LENGTH = 16;

/**
 * Whether a segment looks like an identifier rather than the name of a page.
 *
 * The `@` rule is not about shape, it is about what an address can be. The catch-all route renders a
 * not-found page for ANY path, including one somebody typed or was sent, so a segment here is not
 * always a route this app defines — and an email address in a path is personal data whatever its
 * length. No page of this app has `@` in its name, so redacting it costs nothing.
 */
export function identifierShaped(segment: string): boolean {
	return /^\d+$/.test(segment)
		|| UUID.test(segment)
		|| segment.includes("@")
		|| (segment.length >= MIN_OPAQUE_TOKEN_LENGTH && /\d/.test(segment));
}

/**
 * The pathname with every identifier-shaped or capability-shaped segment replaced.
 *
 * Market prefixes are kept: `/de/pricing` is the German pricing page, not an id, and which market a
 * reader is on is half of what a page report is for. Nothing here spells a locale out — a prefix is
 * simply a segment that is neither named below nor identifier-shaped.
 */
export function redactedPathname(pathname: string): string {
	let identified = false;
	return pathname
		.split("/")
		.map(segment => {
			if (segment === "") {
				return segment;
			}
			if (identified || identifierShaped(segment)) {
				return REDACTED_PATH_SEGMENT;
			}
			identified = IDENTIFIER_BEARING_SEGMENTS.has(segment);
			return segment;
		})
		.join("/");
}

/**
 * The reported address for `href`, or `null` when it is not a URL this can read.
 *
 * It takes the whole href rather than an origin and a pathname on purpose: a caller cannot then be the
 * one that decides to drop the query and the fragment, and a test can feed an address carrying both
 * and assert they are gone.
 */
export function pageLocationOf(href: string): string | null {
	try {
		const url = new URL(href);
		return `${url.origin}${redactedPathname(url.pathname)}`;
	} catch {
		return null;
	}
}

/**
 * The address the LAST accepted report named.
 *
 * It exists because a page view has two triggers, not one: arriving on a page, and being allowed to
 * measure the page you are already on — a visitor who presses Accept on the landing page granted
 * consent without navigating anywhere, and gtag reported that load because `config` runs at the moment
 * the tag starts. Both triggers therefore call this, and this is what stops them counting one page
 * twice. Only the IMMEDIATELY previous address is remembered, so walking away from a page and back
 * counts both visits, exactly as it did before.
 */
let lastReported: string | null = null;

/**
 * Reports one page view, and returns whether it was accepted.
 *
 * `false` covers the three refusals a caller may not distinguish: an address that could not be read, a
 * page already reported, and a visitor who has not granted the analytics purpose. None of them is a
 * page-level error, and none of them is something a caller should try to work around.
 *
 * A report REFUSED for consent is deliberately not remembered, so the grant that follows still reports
 * the page it was given on.
 */
export function reportPageView(href: string, title: string): boolean {
	const pageLocation = pageLocationOf(href);
	if (pageLocation === null) {
		return false;
	}
	// Keyed on the RAW address, never on the redacted one. Two different download capabilities redact
	// to the same `/download/:id`, so deduplicating on what is SENT would silently drop the second
	// recipient page as a repeat of the first. This key never leaves the browser.
	const key = rawKeyOf(href);
	if (key === lastReported) {
		return false;
	}
	// `page_location` first, so it is the parameter that survives if the event is ever truncated.
	if (!sendAnalyticsEvent("PAGE_VIEW", {page_location: pageLocation, page_title: title})) {
		return false;
	}
	lastReported = key;
	return true;
}

/**
 * Forgets the last reported address.
 *
 * Called when the analytics purpose is withdrawn, so a visitor who withdraws and grants again on the
 * same page is reported again rather than being mistaken for a repeat of the visit they revoked.
 */
export function forgetLastReportedPageView(): void {
	lastReported = null;
}

/** The de-duplication key: this browser's own address, which is never sent anywhere. */
function rawKeyOf(href: string): string {
	try {
		const url = new URL(href);
		return `${url.origin}${url.pathname}`;
	} catch {
		return href;
	}
}
